import React, { useState } from 'react';
import { Tab } from '@headlessui/react';
import { 
  ShieldCheckIcon, 
  DevicePhoneMobileIcon, 
  KeyIcon,
  BellIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  FingerPrintIcon
} from '@heroicons/react/24/outline';
import DeviceManagement from '../../components/portal/DeviceManagement';
import TwoFactorSetup from '../../components/auth/TwoFactorSetup';
import WebAuthnManagement from '../../components/portal/WebAuthnManagement';
import { useAuth } from '../../contexts/AuthContextLazy';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase-auth';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function SecuritySettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetupDate, setMfaSetupDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Check if user has MFA enabled
  React.useEffect(() => {
    if (user?.user_metadata?.totp_secret) {
      setMfaEnabled(true);
      // Try to get setup date
      if (user?.user_metadata?.mfa_setup_date) {
        setMfaSetupDate(user.user_metadata.mfa_setup_date);
      } else {
        // If no setup date stored, use current date as fallback
        setMfaSetupDate(new Date().toISOString());
      }
    }
  }, [user]);

  const tabs = [
    { name: 'Trusted Devices', icon: DevicePhoneMobileIcon },
    { name: 'Two-Factor Authentication', icon: ShieldCheckIcon },
    { name: 'Security Keys', icon: KeyIcon },
    { name: 'Security Alerts', icon: BellIcon },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
      {/* Background pattern */}
      <div className="absolute inset-0 -z-10 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23003DA5' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {mfaEnabled ? (
        <div className="portal-alert-success bg-green-50 border border-green-200 rounded-lg p-4 mb-6" style={{
          boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.1), 0 8px 10px -6px rgba(16, 185, 129, 0.05)',
          borderLeft: '4px solid #10B981'
        }}>
          <div className="flex">
            <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Enhanced Security Active</h3>
              <p className="mt-1 text-sm text-green-700">
                Your account is protected with two-factor authentication. Trusted devices can skip verification for faster access.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="portal-alert-warning bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" style={{
          boxShadow: '0 10px 25px -5px rgba(245, 158, 11, 0.1), 0 8px 10px -6px rgba(245, 158, 11, 0.05)',
          borderLeft: '4px solid #F59E0B'
        }}>
          <div className="flex">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">Security Alert</h3>
              <p className="mt-1 text-sm text-amber-700">
                Two-factor authentication is required for all accounts. Please set it up to secure your account.
              </p>
            </div>
          </div>
        </div>
      )}

      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 p-1 shadow-inner">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'portal-tab-active text-white shadow-lg'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800 hover:shadow-sm'
                )
              }
            >
              <div className="flex items-center justify-center gap-2">
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </div>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-6">
          <Tab.Panel className={classNames(
            'rounded-xl bg-white p-3 portal-content-shadow-lg',
            'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
          )}>
            <div className="max-w-4xl mx-auto">
              <DeviceManagement />
            </div>
          </Tab.Panel>
          
          <Tab.Panel className={classNames(
            'rounded-xl bg-white p-6',
            'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
          )} style={{
            boxShadow: '0 20px 40px -10px rgba(0, 61, 165, 0.15), 0 10px 20px -8px rgba(0, 61, 165, 0.1)',
            borderTop: '3px solid #003DA5',
            border: '1px solid rgba(0, 61, 165, 0.1)'
          }}>
            <div className="max-w-4xl mx-auto">
              <div className="portal-card-enhanced portal-accent-border-blue p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Two-Factor Authentication (2FA)
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Add an extra layer of security to your account by requiring a verification code in addition to your password.
                </p>
                
                {mfaEnabled ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <ShieldCheckIcon className="h-8 w-8 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-medium text-green-900">2FA is Active</h4>
                        <p className="text-sm text-green-700">Your account is protected with two-factor authentication.</p>
                        {mfaSetupDate && (
                          <p className="text-xs text-green-600 mt-1">
                            Set up on {new Date(mfaSetupDate).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Authenticator App Status</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Status:</span>
                          <span className="flex items-center gap-1 text-green-600 font-medium">
                            <CheckCircleIcon className="h-4 w-4" />
                            Active
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Method:</span>
                          <span className="text-gray-900">TOTP (Time-based)</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Last verified:</span>
                          <span className="text-gray-900">
                            {sessionStorage.getItem('mfa_verified') === 'true' ? 'This session' : 'Previous session'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-900">Manage 2FA</h4>
                      <div className="space-y-3">
                        <button
                          onClick={() => navigate('/mfa-setup')}
                          className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Re-configure authenticator app</p>
                              <p className="text-xs text-gray-500">Change to a different authenticator app</p>
                            </div>
                          </div>
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Keep your account secure</p>
                          <p>Never share your authenticator codes with anyone. PCM staff will never ask for your 2FA codes.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <ExclamationTriangleIcon className="h-8 w-8 text-amber-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-amber-900">2FA Required</h4>
                        <p className="text-sm text-amber-700">Two-factor authentication is mandatory for all accounts. Set it up now to secure your account.</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Why use 2FA?</h4>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-start gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>Protects against password theft and phishing attacks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>Adds an extra layer of security to your financial data</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>Required for compliance with security best practices</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>Quick setup - takes less than 2 minutes</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      <button
                        onClick={() => navigate('/mfa-setup')}
                        className="flex-1 sm:flex-initial px-6 py-3 portal-btn-primary text-white font-medium rounded-lg flex items-center justify-center gap-2"
                      >
                        <ShieldCheckIcon className="h-5 w-5" />
                        Set Up Two-Factor Authentication
                      </button>
                    </div>
                    
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Supported authenticator apps</p>
                          <p>We recommend Microsoft Authenticator, Google Authenticator, or Authy. All are free and work on iOS and Android.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Tab.Panel>
          
          <Tab.Panel className={classNames(
            'rounded-xl bg-white p-6 portal-content-shadow-lg portal-accent-border-green',
            'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
          )}>
            <div className="max-w-4xl mx-auto">
              <div className="portal-card-enhanced portal-accent-border-green p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Security Keys (WebAuthn)
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Use physical security keys or platform authenticators for the strongest protection against phishing.
                </p>
                <WebAuthnManagement />
              </div>
            </div>
          </Tab.Panel>
          
          <Tab.Panel className={classNames(
            'rounded-xl bg-white p-6 portal-content-shadow-lg portal-accent-border-amber',
            'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
          )}>
            <div className="max-w-4xl mx-auto">
              <div className="portal-card-enhanced portal-accent-border-amber p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Security Alerts
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Configure how you receive notifications about security events on your account.
                </p>
                
                <div className="space-y-4">
                  <label className="flex items-start p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-gray-50 transition-all duration-200 cursor-pointer portal-interactive">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">
                        New device sign-ins
                      </span>
                      <p className="text-sm text-gray-500">
                        Get notified when your account is accessed from a new device
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-start p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-gray-50 transition-all duration-200 cursor-pointer portal-interactive">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">
                        Failed login attempts
                      </span>
                      <p className="text-sm text-gray-500">
                        Alert me about multiple failed login attempts
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-start p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-gray-50 transition-all duration-200 cursor-pointer portal-interactive">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">
                        Security setting changes
                      </span>
                      <p className="text-sm text-gray-500">
                        Notify me when security settings are modified
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-start p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-gray-50 transition-all duration-200 cursor-pointer portal-interactive">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">
                        Monthly security summary
                      </span>
                      <p className="text-sm text-gray-500">
                        Receive a monthly summary of account security activity
                      </p>
                    </div>
                  </label>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button className="px-6 py-3 portal-btn-primary text-white rounded-lg font-medium">
                    Save Alert Preferences
                  </button>
                </div>
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}